<script setup lang="ts">
import {services} from "@/config/services";
import {computed} from "vue";
import {useRoute} from "vue-router";
import {useDataStore} from "~/store/data";
import {marked} from "marked";

const localePath = useLocalePath();
const route = useRoute();
const service = computed(() => {
  return services.value?.find((i: { slug: string }) => i.slug === route.params.slug[0])
});

const {state} = useDataStore()
const {t, locale} = useI18n();

const pricesData = computed(() => {
  return state.prices?.records?.map((record: any) => {
    return {
      size: record.fields.Size,
      price: record.fields.Price
    }
  });
});
const textAfterTable = computed(() => {
  const block = state.blocks?.records?.find((block: any) => {
    return block.fields.id === `block-after-price-${locale.value}`
  })
  return block?.fields?.Text ? marked.parse(block.fields.Text) : '';
});
const textAttention = computed(() => {
  const block = state.blocks?.records?.find((block: any) => {
    return block.fields.id === `block-attention-${locale.value}`
  })
  return block?.fields?.Text ? marked.parse(block.fields.Text) : '';
});
const textPromo = computed(() => {
  const block = state.blocks?.records?.find((block: any) => {
    return block.fields.id === `block-promo-${locale.value}`
  })
  return block?.fields?.Text ? marked.parse(block.fields.Text) : '';
});
useSeoMeta({
  title: t(`seo.service-${service.value?.slug}.title`),
  description: t(`seo.service-${service.value?.slug}.description`),
  ogTitle: t(`seo.service-${service.value?.slug}.title`),
  ogDescription: t(`seo.service-${service.value?.slug}.description`),
  titleTemplate: "%s – Cursus OÜ"
});
</script>
<template>
  <div class="container py-6 lg:py-8">
    <h1 class="font-bold text-3xl lg:text-4xl mb-6">{{ t(service.title) }}</h1>
    <div class="w-full flex flex-col lg:w-7/12">
      <nuxt-img :src="`/img/${service.cover}`" format="webp" quality="90"
                sizes="100vw,sm:50vw,md:75vw,lg:520px,xl:720px"
                width="1320" height="990"
                placeholder
                class="max-h-[556.5px] rounded-xl overflow-hidden" :alt="t(service.title)"/>

      <div class="mt-8">
        <template v-if="locale === 'et'">
          <p class="pb-3">
            Meie ettevõte pakub kõrgkvaliteetseid teemantlõikamise, puurimise ja demonteerimistööde teenuseid. Meie spetsialiseerume keeruliste tööde teostamisele, tagame ohutust, täpsust ja igale projektile professionaalset lähenemist.
          </p>
          <h2 class="text-left font-bold text-3xl lg:text-4xl mt-8 mb-6">Miks meid valitakse</h2>
          <ul class="list-disc pl-6 flex flex-col gap-2">
            <li>
              <strong>Professionaalne lähenemine:</strong> meie spetsialistid on kõrgelt kvalifitseeritud, neil on
              suured kogemused selles valdkonnas ja tagatakse ohutut ja kvaliteetset tööde teostamist.

            </li>
            <li>
              <strong>Innovatiivsed tehnoloogiad:</strong> meie valduses on kaasaegsed seadmed ja tehnikavahendid, mis
              võimaldab meil teostada töid iga projekti puhul ülima täpse ja tõhususega.
              .
            </li>
            <li>
              <strong>Tähtaegadest kinnipidamine: </strong> me hindame teie aega ja teostame töid pidades kinni teiega
              kokku lepitud tähtaegadest, riivamata mingil juhul tööteostuse kvaliteeti.
            </li>
          </ul>
        </template>
        <template v-else>
          <p class="pb-3">
            Наша компания предлагает высококачественные услуги по алмазной резке, бурению и демонтажным работам. Мы
            специализируемся на выполнении сложных работ, обеспечивая безопасность, точность и профессиональный подход к
            каждому проекту.
          </p>
          <h2 class="text-left font-bold text-3xl lg:text-4xl mt-8 mb-6">Почему выбирают нас</h2>
          <ul class="list-disc pl-6 flex flex-col gap-2">
            <li>
              <strong>Профессиональный подход:</strong> Наши специалисты обладают высоким уровнем квалификации и опытом
              в
              данных сферах, гарантируя безопасное и качественное выполнение работ.
            </li>
            <li>
              <strong>Инновационные технологии:</strong> Мы используем передовое оборудование и техники, что позволяет
              нам
              обеспечивать точность и эффективность в каждом проекте.
            </li>
            <li>
              <strong>Соблюдение сроков: </strong> Мы ценим ваше время и стремимся к выполнению работ в оговоренные
              сроки,
              не ущемляя качество выполнения.
            </li>
          </ul>
        </template>
        <div class="flex flex-col gap-2 mt-8 mb-6">
          <h2 class="text-left font-bold text-3xl lg:text-4xl">{{ t('label.prices') }}</h2>
          <div v-if="textPromo && service.slug === 'diamond-drilling'" v-html="textPromo"/>
        </div>
        <template v-if="service.slug === 'diamond-drilling'">
          <div :class="table.root">
            <div :class="table.row">
              <div>№</div>
              <div>{{ t("prices.tableSize") }}</div>
              <div>{{ t("prices.tablePrice") }}</div>
            </div>
            <template v-if="state.loading">
              <div v-for="index in 16" :key="index" :class="table.row">
                <div><span class="animate-pulse mx-auto block h-6 w-6 bg-gray-100 rounded-[6px]"/></div>
                <div><span class="animate-pulse mx-auto block h-6 w-24 bg-gray-100 rounded-[6px]"/></div>
                <div><span class="animate-pulse mx-auto block h-6 w-16 bg-gray-100 rounded-[6px]"/></div>
              </div>
            </template>
            <template v-else>
              <div v-for="(item, index) in pricesData" :key="index" :class="table.row">
                <div>{{ index + 1 }}</div>
                <div>{{ item.size }}</div>
                <div>{{ item.price }}</div>
              </div>
            </template>
          </div>
          <div v-if="state.loading" :class="table.textAfterTable">
            <p class="animate-pulse h-5 bg-gray-100 rounded-[6px]" v-for="index in 10" :key="index"
               :style="`width: ${Math.floor(Math.random() * 5) + 5}0%`"/>
          </div>
          <template v-else>
            <div :class="table.textAfterTable" v-html="textAfterTable"/>
            <div v-if="textAttention" :class="table.textAttention">
              <h3 class="text-xl font-bold mb-3">{{ t("label.attention") }}</h3>
              <div :class="table['textAttention-wrapper']" v-html="textAttention"/>
            </div>
          </template>

        </template>
        <template v-else>
          <p class="mb-6">
            {{ t("label.customPrice1") }}
            <nuxt-link :to="localePath('/contacts')" class="border-b hover:border-black/70 transition-all">
              {{ t("label.customPrice2") }}
            </nuxt-link>
            .
          </p>
        </template>
      </div>
    </div>
  </div>
</template>

<style module="table">
.root {
  @apply flex flex-col w-full border border-solid rounded-[8px] h-fit overflow-hidden;
}

.row {
  @apply w-full grid grid-cols-[40px_minmax(100px,_1fr)_minmax(100px,_26%)] gap-2 items-start justify-between;
  @apply border-b border-solid;
}

.row:first-of-type {
  @apply sticky top-0 font-bold;
}

.row:last-of-type {
  @apply border-none;
}

.row > div {
  @apply text-center p-2 h-full border-r border-solid;
}

.row > div:last-of-type {
  @apply border-none;
}

.textAfterTable {
  @apply text-sm py-6;

  & > ul {
    @apply flex flex-col gap-1 list-disc list-outside pl-6 pb-6;
  }

  & > p {
    @apply mb-1;
  }
}

.textAttention {
  @apply border-2 border-black/60 border-dashed p-5 rounded-[20px] mb-6;

  &-wrapper {
    @apply text-sm;

    & > ul {
      @apply flex flex-col gap-1 list-disc list-outside pl-6 pb-6;
    }

    & > p {
      @apply mb-1;
    }
  }
}
</style>